<template>
  <div class= "min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
      <transition name="view-fade" mode="out-in">
        <div v-if="currentView === 'list'" key="list">
          <header class="mb-8">
             <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 text-center sm:text-left">
                Minhas <span class="text-brand-600 dark:text-brand-500">Metas Compartilhadas</span>
             </h1>
             <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1">
                Poupe em conjunto para alcançar objetivos em comum.
             </p>
          </header>

          <div class="mb-8 flex justify-start">
             <button @click="handleNovaMeta"
                class="btn-primary flex items-center justify-center text-sm shadow-lg hover:shadow-brand-700/50 transition-all">
                <span class="material-symbols-outlined mr-2 text-lg">track_changes</span>
                Criar Nova Meta
             </button>
          </div>

          <div v-if="convitesPendentes.length > 0" class="mb-12">
             <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4 border-b pb-2 border-slate-200 dark:border-slate-700"> Convites Pendentes</h2>
             <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
               <div v-for="convite in convitesPendentes" :key="convite.conviteId" class="bg-white dark:bg-brand-800 p-5 rounded-2xl shadow-lg border-2 border-brand-500 animate-pulse-fast">
                 <p class="text-sm text-slate-600 dark:text-slate-400">
                    <strong class="text-slate-800 dark:text-slate-200">{{ convite.nomeConvidadoPor }}</strong> convidou você para:
                 </p>
                 <h4 class="text-lg font-bold text-brand-600 dark:text-brand-500 mt-1 truncate">{{ convite.nomeEntidade }}</h4>
                 <div class="flex items-center justify-end gap-3 mt-4">
                    <button @click="handleDecline(convite.conviteId)" class="btn-danger-outline !text-xs !py-1.5 !px-3">Recusar</button>
                    <button @click="handleAccept(convite.conviteId)" class="btn-primary !text-xs !py-1.5 !px-3">Aceitar</button>
                 </div>
               </div>
             </div>
          </div>
          <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
             <div v-for="i in 3" :key="i" class="skeleton-card">
               <div class="h-5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded"></div>
               <div class="h-3 w-1/2 bg-slate-200 dark:bg-slate-700 rounded mt-2"></div>
               <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
                 <div class="h-2.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full"></div>
               </div>
             </div>
          </div>
          <div v-else-if="metasAtivas.length === 0 && convitesPendentes.length === 0" class="empty-state">
             <span class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">emoji_events</span>
             <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhuma meta compartilhada encontrada.</p>
             <p class="text-sm">Crie uma nova meta ou aceite um convite para começar.</p>
          </div>
          <transition-group v-else-if="metasAtivas.length > 0" name="list" tag="div" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
             <div v-for="meta in metasAtivas" :key="meta.uuid" @click="selectMeta(meta.uuid)" class="card-meta group">
               <div>
                  <h3 class="font-bold text-xl text-slate-800 dark:text-slate-100 truncate group-hover:text-brand-600 dark:group-hover:text-brand-500 transition-colors"> {{ meta.nome }} </h3>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 h-10 overflow-hidden">{{ meta.descricao }}</p>
                  <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                     <div class="flex justify-between items-baseline mb-2">
                        <span class="text-sm font-medium text-slate-500 dark:text-slate-400">Progresso</span>
                        <span class="text-sm font-bold text-brand-600 dark:text-brand-500">{{ meta.valorAlvo > 0 ? Math.round((meta.valorAtual / meta.valorAlvo) * 100) : 0 }}%</span>
                     </div>
                     <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                        <div class="bg-brand-500 h-2.5 rounded-full transition-all duration-500" :style="{ width: `${meta.valorAlvo > 0 ? Math.min((meta.valorAtual / meta.valorAlvo) * 100, 100) : 0}%` }"></div>
                     </div>
                     <div class="flex justify-between text-xs mt-1.5 text-slate-500 dark:text-slate-400">
                        <span>{{ displayCurrency(meta.valorAtual) }}</span>
                        <span>{{ displayCurrency(meta.valorAlvo) }}</span>
                     </div>
                  </div>
               </div>
             </div>
          </transition-group>

        </div>

        <div v-else-if="currentView === 'details' && selectedMeta" key="details">
          <button @click="returnToList"
            class="mb-4 flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-500/80 transition-colors">
            <span class="material-symbols-outlined mr-1">arrow_back</span>Voltar para Lista
          </button>

          <header class="bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg mb-8">
            <div class="md:flex justify-between items-start">
               <div>
                  <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">{{ selectedMeta.nome }}</h1>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">{{ selectedMeta.descricao }}</p>
               </div>
               <div class="flex flex-wrap gap-3 mt-4 md:mt-0 flex-shrink-0">
                  <button @click="showAddContribuicaoModal = true" class="btn-primary flex-1 sm:flex-none">
                     <span class="material-symbols-outlined mr-2 text-lg">savings</span> Contribuir
                  </button>
                  <button v-if="isOwner" @click="showInviteModal = true" class="btn-secondary flex-1 sm:flex-none">
                     <span class="material-symbols-outlined mr-2 text-lg">person_add</span> Convidar
                  </button>
                  <button v-if="isOwner" @click="handleEditMeta" class="btn-secondary flex-1 sm:flex-none">
                     <span class="material-symbols-outlined mr-2 text-lg">edit</span> Editar
                  </button>
               </div>
            </div>
            <div class="mt-6">
                <div class="flex justify-between items-center mb-1 text-slate-600 dark:text-slate-300">
                    <span class="font-medium">Progresso da Meta</span>
                    <span class="font-bold">{{ selectedMeta.valorAlvo > 0 ?
                        Math.round((selectedMeta.valorAtual / selectedMeta.valorAlvo) * 100) : 0 }}%</span>
                </div>
                <div class="w-full bg-slate-200 dark:bg-brand-900/70 rounded-full h-4 mb-2">
                    <div class="bg-gradient-to-r from-green-400 to-sky-400 h-4 rounded-full"
                        :style="{ width: `${selectedMeta.valorAlvo > 0 ? Math.min((selectedMeta.valorAtual / selectedMeta.valorAlvo) * 100, 100) : 0}%` }">
                    </div>
                </div>
                <div class="flex justify-between items-baseline">
                    <span class="text-2xl font-bold text-slate-800 dark:text-white">{{
                        displayCurrency(selectedMeta.valorAtual) }}</span>
                    <span class="text-sm text-slate-500 dark:text-slate-400">de {{
                        displayCurrency(selectedMeta.valorAlvo) }}</span>
                </div>
            </div>
          </header>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
                <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Histórico de
                    Contribuições</h2>
                <div v-if="!selectedMeta.contribuicoes || selectedMeta.contribuicoes.length === 0"
                    class="empty-state flex-col justify-center">
                    <span
                        class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">receipt_long</span>
                    <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhuma contribuição
                        registrada.</p>
                </div>
                <div v-else
                    class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg max-h-[600px] overflow-y-auto p-6">
                    <div class="relative timeline-container">
                        <div v-for="item in selectedMeta.contribuicoes" :key="item.id"
                            class="timeline-item group">
                            <div class="timeline-point"></div>
                            <div class="timeline-content">
                                <div class="flex items-center justify-between">
                                    <div>
                                        <p class="font-semibold text-slate-800 dark:text-slate-100">{{
                                            displayCurrency(item.valor) }}</p>
                                        <p class="text-sm text-slate-500 dark:text-slate-400">por {{
                                            item.usuarioNome }}</p>
                                        <p class="text-xs text-slate-400 dark:text-slate-500">{{
                                            formatDateTime(item.dataContribuicao) }}</p>
                                    </div>
                                    <div
                                        class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button v-if="isOwner || item.usuarioId === currentUser.id"
                                            @click="openEditContribuicaoModal(item)"
                                            class="btn-action-icon text-sky-500 dark:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                                            title="Editar contribuição">
                                            <span class="material-symbols-outlined text-lg">edit</span>
                                        </button>
                                        <button v-if="isOwner || item.usuarioId === currentUser.id"
                                            @click="confirmDeleteContribuicao(item)"
                                            class="btn-action-icon text-red-500 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                                            title="Remover contribuição">
                                            <span class="material-symbols-outlined text-lg">delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Membros</h2>
                <div v-if="!selectedMeta.membros || selectedMeta.membros.length === 0"
                    class="empty-state h-full flex-col justify-center">
                    <span
                        class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">group_off</span>
                    <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum membro encontrado.
                    </p>
                </div>
                <div v-else
                    class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg max-h-[600px] overflow-y-auto">
                    <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                        <li v-for="membro in selectedMeta.membros" :key="membro.id"
                            class="p-4 flex items-center justify-between transition-opacity"
                            :class="{ 'opacity-60': membro.status === 'PENDENTE' }">
                            <div class="flex items-center">
                                <div
                                    class="w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-700 text-brand-600 dark:text-brand-500 flex items-center justify-center text-sm font-bold ring-2 ring-white dark:ring-brand-800 flex-shrink-0">
                                    {{ membro.nome.charAt(0).toUpperCase() }}
                                </div>
                                <div class="ml-4">
                                    <p
                                        class="font-semibold text-slate-800 dark:text-slate-100 flex items-center">
                                        {{ membro.nome }}
                                        <span v-if="membro.id === selectedMeta.criadorId"
                                            class="tag-owner">Dono</span>
                                        <span v-if="membro.status === 'PENDENTE'"
                                            class="tag-pending">Pendente</span>
                                    </p>
                                    <p class="text-sm text-slate-500 dark:text-slate-400">{{ membro.email }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-center">
                                <button
                                    v-if="isOwner && membro.id !== currentUser.id && membro.status === 'ATIVO'"
                                    @click="confirmRemoveMember(membro)"
                                    class="btn-danger-outline !p-2 !rounded-full" title="Remover membro">
                                    <span class="material-symbols-outlined text-lg">person_remove</span>
                                </button>
                                <button
                                    v-if="isOwner && membro.id !== currentUser.id && membro.status === 'PENDENTE'"
                                    @click="confirmCancelInvite(membro)"
                                    class="btn-secondary !p-2 !rounded-full" title="Cancelar convite">
                                    <span class="material-symbols-outlined text-lg">cancel</span>
                                </button>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
          </div>
          <br/><br/><br/>
          <div class="lg:col-span-2 mt-4">
            <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Zona de Perigo</h2>
            <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div v-if="isOwner">
                    <h4 class="font-bold text-red-800 dark:text-red-300">Excluir Meta</h4>
                    <p class="text-sm text-red-600 dark:text-red-300/80">Esta ação é irreversível e excluirá todas as contribuições.</p>
                </div>
                <button v-if="isOwner" @click="confirmDeleteMeta" class="btn-danger-outline flex items-center text-sm flex-shrink-0">
                    <span class="material-symbols-outlined mr-2 text-lg">delete_forever</span> Excluir Meta
                </button>

                <div v-if="!isOwner">
                    <h4 class="font-bold text-red-800 dark:text-red-300">Sair da Meta</h4>
                    <p class="text-sm text-red-600 dark:text-red-300/80">Você perderá o acesso a esta meta e suas contribuições.</p>
                </div>
                <button v-if="!isOwner" @click="confirmLeaveMeta" class="btn-danger-outline flex items-center text-sm flex-shrink-0">
                    <span class="material-symbols-outlined mr-2 text-lg">logout</span> Sair da Meta
                </button>
            </div>
          </div>

        </div>
      </transition>
    </div>

    <transition name="modal-fade">
      <div v-if="showCreateModal" @click.self="showCreateModal = false" class="modal-backdrop">
        <div class="modal-container">
           <header class="modal-header">
              <h3>Criar Nova Meta</h3><button @click="showCreateModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
           </header>
           <form @submit.prevent="handleCreateMeta" class="p-6 space-y-4">
              <div><label for="pNome" class="form-label">Nome da Meta</label><input id="pNome" v-model="newMeta.nome" type="text" required placeholder="Ex: Viagem ao Japão, Entrada do Apartamento" class="form-input" /></div>
              <div><label for="pDesc" class="form-label">Descrição</label><input id="pDesc" v-model="newMeta.descricao" type="text" placeholder="Detalhes e objetivos" class="form-input" /></div>
              <div><label for="pValorAlvo" class="form-label">Valor Alvo (R$)</label><input id="pValorAlvo" v-model.number="newMeta.valorAlvo" type="number" step="0.01" required placeholder="5000.00" class="form-input" /></div>
              <div><label for="pEmails" class="form-label">Convidar Membros (opcional)</label><textarea id="pEmails" v-model="newMeta.emailsMembros" rows="3" placeholder="Separe os emails por vírgula ou espaço" class="form-input"></textarea></div>
              <footer class="modal-footer"><button @click="showCreateModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Criando...' : 'Criar Meta' }}</button></footer>
           </form>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showEditMetaModal" @click.self="showEditMetaModal = false" class="modal-backdrop">
        <div class="modal-container">
           <header class="modal-header">
              <h3>Editar Meta</h3><button @click="showEditMetaModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
           </header>
           <form @submit.prevent="handleUpdateMeta" class="p-6 space-y-4">
              <div><label for="pEditNome" class="form-label">Nome da Meta</label><input id="pEditNome" v-model="editMetaForm.nome" type="text" required class="form-input" /></div>
              <div><label for="pEditDesc" class="form-label">Descrição</label><input id="pEditDesc" v-model="editMetaForm.descricao" type="text" class="form-input" /></div>
              <div><label for="pEditValorAlvo" class="form-label">Valor Alvo (R$)</label><input id="pEditValorAlvo" v-model.number="editMetaForm.valorAlvo" type="number" step="0.01" required class="form-input" /></div>
              <footer class="modal-footer"><button @click="showEditMetaModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Salvando...' : 'Salvar Alterações' }}</button></footer>
           </form>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
       <div v-if="showInviteModal" @click.self="showInviteModal = false" class="modal-backdrop">
         <div class="modal-container">
            <header class="modal-header">
               <h3>Convidar Membro</h3><button @click="showInviteModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
            </header>
            <form @submit.prevent="handleInviteMember" class="p-6 space-y-4">
               <div><label for="inviteEmail" class="form-label">Email do Convidado</label><input id="inviteEmail" v-model="inviteForm.email" type="email" required placeholder="nome@exemplo.com" class="form-input" /></div>
               <footer class="modal-footer"><button @click="showInviteModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Convidando...' : 'Enviar Convite' }}</button></footer>
            </form>
         </div>
       </div>
    </transition>
    <transition name="modal-fade">
       <div v-if="showAddContribuicaoModal" @click.self="showAddContribuicaoModal = false" class="modal-backdrop">
         <div class="modal-container">
            <header class="modal-header">
               <h3>Adicionar Contribuição</h3><button @click="showAddContribuicaoModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
            </header>
            <form @submit.prevent="handleAddContribuicao" class="p-6 space-y-4">
               <div><label for="cValor" class="form-label">Valor da Contribuição (R$)</label><input id="cValor" v-model.number="contribuicaoForm.valor" type="number" step="0.01" required placeholder="100.00" class="form-input" /></div>
               <footer class="modal-footer"><button @click="showAddContribuicaoModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Adicionando...' : 'Adicionar' }}</button></footer>
            </form>
         </div>
       </div>
    </transition>
    <transition name="modal-fade">
       <div v-if="showEditContribuicaoModal" @click.self="showEditContribuicaoModal = false" class="modal-backdrop">
         <div class="modal-container">
            <header class="modal-header">
               <h3>Editar Contribuição</h3><button @click="showEditContribuicaoModal = false" class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
            </header>
            <form @submit.prevent="handleUpdateContribuicao" class="p-6 space-y-4">
               <div><label for="ecValor" class="form-label">Novo Valor (R$)</label><input id="ecValor" v-model.number="editContribuicaoForm.valor" type="number" step="0.01" required placeholder="100.00" class="form-input" /></div>
               <footer class="modal-footer"><button @click="showEditContribuicaoModal = false" type="button" class="btn-secondary">Cancelar</button><button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center"><span v-if="isSubmitting" class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{ isSubmitting ? 'Salvando...' : 'Salvar' }}</button></footer>
            </form>
         </div>
       </div>
    </transition>
    <transition name="modal-fade">
       <div v-if="showConfirmDeleteMeta" @click.self="showConfirmDeleteMeta = false" class="modal-backdrop">
         <div class="modal-container max-w-md p-0">
            <div class="p-6 text-center"><span class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">delete_forever</span>
               <h3 class="modal-title">Excluir Meta?</h3>
               <p class="modal-text">Esta ação é irreversível e excluirá <strong>todas as contribuições</strong> dentro de "{{ selectedMeta?.nome }}".</p>
            </div>
            <div class="modal-footer"><button @click="showConfirmDeleteMeta = false" type="button" class="btn-secondary">Cancelar</button><button @click="executeDeleteMeta" class="btn-danger">Sim, Excluir</button></div>
         </div>
       </div>
    </transition>
    <transition name="modal-fade">
       <div v-if="showConfirmRemoveMember" @click.self="showConfirmRemoveMember = false" class="modal-backdrop">
         <div class="modal-container max-w-md p-0">
            <div class="p-6 text-center"><span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">person_remove</span>
               <h3 class="modal-title">Remover Membro?</h3>
               <p class="modal-text">Tem certeza que deseja remover <strong>{{ memberToRemove?.nome }}</strong> da meta?</p>
            </div>
            <div class="modal-footer"><button @click="showConfirmRemoveMember = false" type="button" class="btn-secondary">Cancelar</button><button @click="executeRemoveMember" class="btn-danger">Sim, Remover</button></div>
         </div>
       </div>
    </transition>
    <transition name="modal-fade">
       <div v-if="showConfirmLeave" @click.self="showConfirmLeave = false" class="modal-backdrop">
         <div class="modal-container max-w-md p-0">
            <div class="p-6 text-center"><span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">logout</span>
               <h3 class="modal-title">Sair da Meta?</h3>
               <p class="modal-text">Tem certeza que deseja sair da meta <strong>"{{ selectedMeta?.nome }}"</strong>?</p>
            </div>
            <div class="modal-footer"><button @click="showConfirmLeave = false" type="button" class="btn-secondary">Cancelar</button><button @click="executeLeaveMeta" class="btn-danger">Sim, Sair</button></div>
         </div>
       </div>
    </transition>
    <transition name="modal-fade">
       <div v-if="showConfirmDeleteContribuicao" @click.self="showConfirmDeleteContribuicao = false" class="modal-backdrop">
         <div class="modal-container max-w-md p-0">
            <div class="p-6 text-center"><span class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">warning</span>
               <h3 class="modal-title">Excluir Contribuição?</h3>
               <p class="modal-text">Tem certeza que deseja excluir esta contribuição de <strong>{{ displayCurrency(contribuicaoToDelete?.valor) }}</strong>? O valor será abatido do total.</p>
            </div>
            <div class="modal-footer"><button @click="showConfirmDeleteContribuicao = false" type="button" class="btn-secondary">Cancelar</button><button @click="executeDeleteContribuicao" class="btn-danger">Sim, Excluir</button></div>
         </div>
       </div>
    </transition>
    <transition name="modal-fade">
       <div v-if="showConfirmCancelInvite" @click.self="showConfirmCancelInvite = false" class="modal-backdrop">
         <div class="modal-container max-w-md p-0">
            <div class="p-6 text-center">
               <span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">cancel</span>
               <h3 class="modal-title">Cancelar Convite?</h3>
               <p class="modal-text">Tem certeza que deseja cancelar o convite enviado para <strong>{{ inviteToCancel?.nome }}</strong>?</p>
            </div>
            <div class="modal-footer">
               <button @click="showConfirmCancelInvite = false" type="button" class="btn-secondary">Voltar</button>
               <button @click="executeCancelInvite" class="btn-danger">Sim, Cancelar</button>
            </div>
         </div>
       </div>
    </transition>

    <transition name="notification">
        <div v-if="notification.show" class="hidden md:block fixed top-5 right-5 w-full max-w-sm z-[70]">
            <div class="p-4 rounded-lg shadow-2xl flex items-start text-white"
                :class="notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'">
                <div class="flex-shrink-0"><span class="material-symbols-outlined">{{ notification.type === 'success' ?
                    'check_circle' : 'error' }}</span></div>
                <div class="ml-3 flex-1">
                    <p class="text-sm font-bold">{{ notification.message }}</p>
                </div>
                <div class="ml-auto pl-3"><button @click="notification.show = false"
                        class="-mx-1.5 -my-1.5 p-1.5 rounded-md hover:bg-black/20 focus:outline-none focus:ring-2 focus:ring-white"><span
                            class="material-symbols-outlined text-lg">close</span></button></div>
            </div>
        </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const router = useRouter();
const metasAtivas = ref([]);
const convitesPendentes = ref([]);
const selectedMeta = ref(null);
const currentUser = ref({ id: null });
const currentView = ref('list');
const isLoading = ref(true);
const isSubmitting = ref(false);

const isMobile = ref(false);
const checkDevice = () => {
  isMobile.value = window.innerWidth < 768; 
};

const showCreateModal = ref(false);
const showInviteModal = ref(false);
const showEditMetaModal = ref(false);
const showAddContribuicaoModal = ref(false);
const showEditContribuicaoModal = ref(false);

const showConfirmDeleteMeta = ref(false);
const showConfirmRemoveMember = ref(false);
const showConfirmLeave = ref(false);
const showConfirmDeleteContribuicao = ref(false);
const showConfirmCancelInvite = ref(false);

const memberToRemove = ref(null);
const contribuicaoToDelete = ref(null);
const contribuicaoToEdit = ref(null);
const inviteToCancel = ref(null);
const newMeta = ref({ nome: '', descricao: '', valorAlvo: null, emailsMembros: '' });
const inviteForm = ref({ email: '' });
const editMetaForm = ref({ nome: '', descricao: '', valorAlvo: null });
const contribuicaoForm = ref({ valor: null });
const editContribuicaoForm = ref({ valor: null });

const notification = ref({ show: false, type: 'success', message: '' });
const notificationTimer = ref(null);

const API_BASE_URL = 'https://backend.controlease.com.br/api';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const isOwner = computed(() => (currentUser.value?.id && selectedMeta.value?.criadorId) ? currentUser.value.id === selectedMeta.value.criadorId : false);

const handleNovaMeta = () => {
    if (isMobile.value) {
        router.push({ name: 'MetasCompartilhadasAdd' });
    } else {
        openCreateMetaModal(); 
    }
};

const handleEditMeta = () => { 
    if (!selectedMeta.value) return;
    if (isMobile.value) {
        router.push({ name: 'MetasCompartilhadasEdit', params: { uuid: selectedMeta.value.uuid } });
    } else {
        openEditMetaModal(); 
    }
};

const showNotification = (type, message, duration = 4000) => {
    if (notificationTimer.value) clearTimeout(notificationTimer.value);
    notification.value = { show: true, type, message };
    notificationTimer.value = setTimeout(() => { notification.value.show = false; }, duration);
};

const displayCurrency = (value) => value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00';
const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    if (isNaN(date)) return '';
    return date.toLocaleString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fetchMetas = async () => {
    isLoading.value = true;
    try {
        const response = await axios.get(`${API_BASE_URL}/metas-compartilhadas`, authConfig());
        metasAtivas.value = response.data.metasAtivas || [];
        convitesPendentes.value = response.data.convitesPendentes || [];
    } catch (error) { showNotification('error', "Não foi possível carregar suas metas."); }
    finally { isLoading.value = false; }
};

const handleAccept = async (conviteId) => {
    try {
        await axios.post(`${API_BASE_URL}/convites/${conviteId}/aceitar`, {}, authConfig());
        showNotification('success', "Convite aceito com sucesso!");
        fetchMetas();
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao aceitar convite."); }
};

const handleDecline = async (conviteId) => {
    try {
        await axios.post(`${API_BASE_URL}/convites/${conviteId}/recusar`, {}, authConfig());
        showNotification('success', "Convite recusado.");
        convitesPendentes.value = convitesPendentes.value.filter(c => c.conviteId !== conviteId);
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao recusar convite."); }
};

const handleInviteMember = async () => {
    isSubmitting.value = true;
    try {
        await axios.post(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}/convidar`, inviteForm.value, authConfig());
        await refreshSelectedMeta();
        showInviteModal.value = false;
        inviteForm.value.email = '';
        showNotification('success', 'Convite enviado com sucesso!');
    } catch (error) { showNotification('error', error.response?.data?.message || "Não foi possível convidar o membro."); }
    finally { isSubmitting.value = false; }
};

const fetchCurrentUser = () => {
    const userId = localStorage.getItem('usuario_id');
    if (userId) { currentUser.value = { id: parseInt(userId, 10) }; }
};

const selectMeta = async (uuid) => {
    isLoading.value = true;
    try {
        const response = await axios.get(`${API_BASE_URL}/metas-compartilhadas/${uuid}`, authConfig());
        selectedMeta.value = response.data;
        currentView.value = 'details';
        if (route.params.id !== uuid) {
             router.push({ name: 'MetasCompartilhadas', params: { id: uuid } });
        }
    } catch (error) {
        showNotification('error', "Não foi possível carregar os detalhes da meta.");
        router.push({ name: 'MetasCompartilhadas' });
    } finally { isLoading.value = false; }
};

const returnToList = async () => {
    currentView.value = 'list';
    selectedMeta.value = null;
    router.push({ name: 'MetasCompartilhadas', params: { id: undefined } });
    await fetchMetas();
};

const handleCreateMeta = async () => {
    isSubmitting.value = true;
    try {
        const payload = { ...newMeta.value, emailsMembros: newMeta.value.emailsMembros.split(/[\s,]+/).filter(Boolean) };
        const response = await axios.post(`${API_BASE_URL}/metas-compartilhadas`, payload, authConfig());
        showCreateModal.value = false;
        newMeta.value = { nome: '', descricao: '', valorAlvo: null, emailsMembros: '' };
        await fetchMetas();
        if (response.data.uuid) {
            await selectMeta(response.data.uuid); 
        }
        showNotification('success', 'Meta criada e convites enviados!');
    } catch (error) { showNotification('error', error.response?.data?.message || "Ocorreu um erro ao criar a meta."); }
    finally { isSubmitting.value = false; }
};

const openCreateMetaModal = () => { 
    newMeta.value = { nome: '', descricao: '', valorAlvo: null, emailsMembros: '' }; 
    showCreateModal.value = true;
};

const openEditMetaModal = () => { 
    if (!selectedMeta.value) return;
    editMetaForm.value = { nome: selectedMeta.value.nome, descricao: selectedMeta.value.descricao, valorAlvo: selectedMeta.value.valorAlvo };
    showEditMetaModal.value = true;
};

const handleUpdateMeta = async () => { 
    isSubmitting.value = true;
    try {
        await axios.put(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}`, editMetaForm.value, authConfig());
        await refreshSelectedMeta();
        showEditMetaModal.value = false;
        showNotification('success', 'Meta atualizada com sucesso!');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao atualizar meta."); }
    finally { isSubmitting.value = false; }
};

const confirmDeleteMeta = () => { showConfirmDeleteMeta.value = true; };
const executeDeleteMeta = async () => {
    showConfirmDeleteMeta.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}`, authConfig());
        await returnToList();
        showNotification('success', 'Meta excluída com sucesso.');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao excluir meta."); }
};

const confirmRemoveMember = (membro) => { memberToRemove.value = membro; showConfirmRemoveMember.value = true; };
const executeRemoveMember = async () => {
    if (!memberToRemove.value) return;
    showConfirmRemoveMember.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}/membros/${memberToRemove.value.id}`, authConfig());
        await refreshSelectedMeta();
        showNotification('success', 'Membro removido com sucesso.');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao remover membro."); }
    finally { memberToRemove.value = null; }
};

const confirmLeaveMeta = () => { showConfirmLeave.value = true; };
const executeLeaveMeta = async () => {
    showConfirmLeave.value = false;
    try {
        await axios.post(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}/membros/sair`, {}, authConfig());
        await returnToList();
        showNotification('success', 'Você saiu da meta.');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao sair da meta."); }
};

const handleAddContribuicao = async () => {
    isSubmitting.value = true;
    try {
        await axios.post(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}/contribuicoes`, contribuicaoForm.value, authConfig());
        await refreshSelectedMeta();
        showAddContribuicaoModal.value = false;
        contribuicaoForm.value = { valor: null };
        showNotification('success', 'Contribuição adicionada com sucesso!');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao adicionar contribuição."); }
    finally { isSubmitting.value = false; }
};

const openEditContribuicaoModal = (contribuicao) => {
    contribuicaoToEdit.value = contribuicao;
    editContribuicaoForm.value.valor = contribuicao.valor;
    showEditContribuicaoModal.value = true;
};
const handleUpdateContribuicao = async () => {
    if (!contribuicaoToEdit.value) return;
    isSubmitting.value = true;
    try {
        await axios.put(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}/contribuicoes/${contribuicaoToEdit.value.id}`, editContribuicaoForm.value, authConfig());
        await refreshSelectedMeta();
        showEditContribuicaoModal.value = false;
        contribuicaoToEdit.value = null;
        showNotification('success', 'Contribuição atualizada com sucesso!');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao atualizar contribuição."); }
    finally { isSubmitting.value = false; }
};

const confirmDeleteContribuicao = (contribuicao) => { contribuicaoToDelete.value = contribuicao; showConfirmDeleteContribuicao.value = true; };
const executeDeleteContribuicao = async () => {
    if (!contribuicaoToDelete.value) return;
    showConfirmDeleteContribuicao.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}/contribuicoes/${contribuicaoToDelete.value.id}`, authConfig());
        await refreshSelectedMeta();
        showNotification('success', 'Contribuição removida com sucesso.');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao remover contribuição."); }
    finally { contribuicaoToDelete.value = null; }
};

const confirmCancelInvite = (membro) => { inviteToCancel.value = membro; showConfirmCancelInvite.value = true; };
const executeCancelInvite = async () => {
    if (!inviteToCancel.value?.conviteId) return;
    showConfirmCancelInvite.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/convites/${inviteToCancel.value.conviteId}/cancelar`, authConfig());
        await refreshSelectedMeta();
        showNotification('success', 'Convite cancelado com sucesso.');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao cancelar o convite."); }
    finally { inviteToCancel.value = null; }
};

const refreshSelectedMeta = async () => {
    if (!selectedMeta.value?.uuid) return;
    try {
        const response = await axios.get(`${API_BASE_URL}/metas-compartilhadas/${selectedMeta.value.uuid}`, authConfig());
        selectedMeta.value = response.data;
    } catch (e) {
        showNotification('error', 'Erro ao atualizar dados da meta.');
        returnToList();
    }
};

onMounted(() => {
    fetchCurrentUser();
    const metaId = route.params.id; 
    if (metaId) {
        selectMeta(metaId); 
    } else {
        fetchMetas();
        currentView.value = 'list'; 
    }
    checkDevice();
    window.addEventListener('resize', checkDevice);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkDevice);
    if (notificationTimer.value) clearTimeout(notificationTimer.value);
});
</script>

<style scoped>
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1; }
.form-input { @apply w-full px-3 py-2 text-sm bg-white dark:bg-slate-700/60 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200; }
.btn-primary { @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed; }
.btn-secondary { @apply px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed; }
.btn-danger { @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out; }
.btn-danger-outline { @apply px-5 py-2.5 text-sm font-medium text-red-600 bg-transparent border border-red-500 hover:bg-red-50 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed; }
.btn-action-icon { @apply p-1.5 rounded-full transition-colors duration-200; }
.card-meta { @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg flex flex-col justify-between transition-all duration-300 hover:shadow-xl dark:hover:shadow-brand-700/30 cursor-pointer hover:-translate-y-1; }
.empty-state { @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col items-center justify-center; }
.skeleton-card { @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg animate-pulse space-y-4; }
.tag-owner { @apply ml-2 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-brand-700 dark:text-brand-500 px-2 py-0.5 rounded-full; }
.tag-pending { @apply ml-2 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-0.5 rounded-full; }
.modal-backdrop { @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 dark:bg-brand-900/70 backdrop-blur-sm p-4; }
.modal-container { @apply bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg transform transition-all; }
.modal-header { @apply px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center text-xl font-semibold text-slate-800 dark:text-slate-100; }
.modal-close-btn { @apply p-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors; }
.modal-footer { @apply p-4 bg-slate-50 dark:bg-slate-700/50 flex justify-end space-x-3 rounded-b-xl; }
.modal-title { @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100; }
.modal-text { @apply mt-2 text-sm text-slate-600 dark:text-slate-400; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; transform: scale(0.95) translateY(-20px); }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.view-fade-enter-active, .view-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.view-fade-enter-from { opacity: 0; transform: translateX(-10px); }
.view-fade-leave-to { opacity: 0; transform: translateX(10px); }
.list-enter-active, .list-leave-active { transition: all 0.4s ease; }
.list-enter-from, .list-leave-to { opacity: 0; transform: translateY(20px); }
.timeline-container { @apply border-l-2 border-slate-200 dark:border-slate-700; }
.timeline-item { @apply relative pl-8 py-4; }
.timeline-point { @apply bg-green-500 absolute -left-[9px] top-[28px] w-4 h-4 rounded-full border-4 border-white dark:border-brand-800; }
.timeline-content { @apply ml-4; }
.notification-enter-active, .notification-leave-active { transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1); }
.notification-enter-from, .notification-leave-to { opacity: 0; transform: translateX(100%); }
.animate-pulse-fast { animation: pulse-fast 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
@keyframes pulse-fast { 50% { opacity: .85; } }
</style>